/**
 * Provides observe and fire methods on subclasses.
 * @constructor
 */
Foundation.Observable = function () {
    /**
     * The datastore. This is where we put the stuff that set() and get()
     * methods work with.
     * @type {Object.<string,*>!}
     * @private
     */
    this._observableData = {};

    /**
     * 
     * @type {Object.<string,Array.<Foundation.Observable.Event.Handler>>}
     * @private
     */
    this._observers = {};
    
    /**
     * @type {Object.<string,Object.<string,Array.<Foundation.Observable.Event.Handler>>>}
     * @private
     */
    this._observersByObj = {};
    
    /**
     * @type {string?}
     * @private
     */
    this._observableId = null;

    /**
     * @type {Foundation.Promise}
     * @private
     */
    this._destructorPromise = null;

    /**
     * @type {boolean}
     * @private
     */
    this._hasBeenDestroyed = false;
};

/**
 * Getter.
 * @param {string} field
 */
Foundation.Observable.prototype.get = function (field) {
    return this._observableData[field];
};

/**
 * Setter.
 * @param {string} field
 * @param {*} value
 * @return {Foundation.Observable}
 */
Foundation.Observable.prototype.set = function (field, value) {
    var eData = Foundation.Observable.changedEventData(field, this._observableData[field], value);
    this._observableData[field] = value;
    this.fire("changed", eData);
    return this;
};

/**
 * Unsetter.
 * @param {string} field
 * @return {Foundation.Observable}
 */
Foundation.Observable.prototype.unset = function (field) {
    var eData = Foundation.Observable.changedEventData(field, this._observableData[field], undefined);
    delete this._observableData[field];
    this.fire("changed", eData);
    return this;
};


/**
 * Returns a list of fields that are set.
 * @return {Array.<string>}
 */
Foundation.Observable.prototype.fields = function () {
    return /** @type {Array.<string>} */ (Object.keys(this._observableData));
};

/**
 * Return the number of fields that have been set.
 * @return {number}
 */
Foundation.Observable.prototype.size = function () {
    return this.fields().length;
};

/**
 * Multi-field setter.
 * @param {Object.<string,*>|Foundation.Observable} obj
 * @return {Foundation.Observable}
 */
Foundation.Observable.prototype.extend = function (obj) {
    var data;
    
    if (obj instanceof Foundation.Observable) {
        data = obj._observableData;
    }
    else {
        data = obj;
    }
    
    Foundation.extend(this._observableData, data);
    
    this.fire("changed");
    return this;
};

/**
 * Export observable data into a standard JSON-compatible object.
 * @return {Object.<string,*>}
 */
Foundation.Observable.prototype.plainObject = function () {
    var obj = {};
    for (var key in this._observableData) {
        obj[key] = Foundation.Observable._export(this._observableData[key]);
    }
    return obj;
};

/**
 * Simply an alias of the plainObject method.
 * @return {Object.<string,*>}
 */
Foundation.Observable.prototype.toJSON = Foundation.Observable.prototype.plainObject;

/**
 * @private
 */
Foundation.Observable._export = function (val) {
    if (val instanceof Foundation.Observable) {
        return val.plainObject();
    }
    else if (Array.isArray(val)) {
        var arr = [];
        for (var i = 0; i < val.length; i++) {
            arr.push(Foundation.Observable._export(val[i]));
        }
        return arr;
    }
    else if (val === null || val === undefined || Foundation.isString(val) || typeof val !== "object") {
        return val;
    }
    else if (val.constructor === /x/.constructor) {
        return val.toString();
    }
    else {
        var obj = {};
        for (var key in val) {
            if (Object.prototype.hasOwnProperty.call(val, key)) {
                obj[key] = Foundation.Observable._export(val[key]);
            }
        }
        return obj;
    }
};

/**
 * @private
 * @type {number}
 */
Foundation.Observable.identifierIncrement = 0;

/**
 * Generate an identifier string for this object.
 * @return {string}
 */
Foundation.Observable.prototype.identify = function () {
    if (!this._observableId) {
        this._observableId = `Foundation.Observable.id.${Foundation.Observable.identifierIncrement++}`;
    }
    return this._observableId;
};

/**
 * Register event observers.
 * @param {string} eventName
 * @param {Foundation.Observable.Event.Handler} callback
 * @return {Foundation.Observable}
 */
Foundation.Observable.prototype.observe = function (eventName, callback) {
    if (!this._observers[eventName]) this._observers[eventName] = [];
    this._observers[eventName].push(callback);
    return this;
};

/**
 * Stop observing an event. The callback must be exactly the same Function
 * object that was passed to the observe() method. See bind() and unbind()
 * to make that process easier.
 * @param {string} eventName
 * @param {Foundation.Observable.Event.Handler} callback
 * @return {Foundation.Observable}
 */
Foundation.Observable.prototype.unobserve = function (eventName, callback) {
    var observers = this._observers[eventName] || [];
    
    this._observers[eventName] = [];
    for (var i = 0; i < observers.length; i++) {
        if (observers[i] !== callback) this._observers[eventName].push(observers[i]);
    }
    
    return this;
};

/**
 * Remove all observers of an event.
 * @param {string} eventName
 * @return {Foundation.Observable}
 */
Foundation.Observable.prototype.unobserveAll = function (eventName) {
    delete this._observers[eventName];
    return this;
};

/**
 * Register an object and method to receive events.
 * @param {string} eventName
 * @param {Foundation.Observable} contextObj
 * @param {Foundation.Observable.Event.Handler} method
 * @return {Foundation.Observable}
 */
Foundation.Observable.prototype.bind = function (eventName, contextObj, method) {
    var callback = method.bind(contextObj);
    
    if (!this._observersByObj[eventName]) {
        this._observersByObj[eventName] = {};
    }
    
    if (!this._observersByObj[eventName][contextObj.identify()]) {
        this._observersByObj[eventName][contextObj.identify()] = [];
    }
    
    this._observersByObj[eventName][contextObj.identify()].push(callback);
    
    return this.observe(eventName, callback);
};

/**
 * Stop an object's methods from receiving events.
 * @param {string} eventName
 * @param {Foundation.Observable} contextObj
 * @return {Foundation.Observable}
 */
Foundation.Observable.prototype.unbind = function (eventName, contextObj) {
    if (this._observersByObj[eventName] && this._observersByObj[eventName][contextObj.identify()]) {
        var callbacks = this._observersByObj[eventName][contextObj.identify()];
        for (var i = 0; i < callbacks.length; i++) {
            this.unobserve(eventName, callbacks[i]);
        }
        delete this._observersByObj[eventName][contextObj.identify()];
    }
    
    return this;
};

/**
 * Register an event observer that ensures the callback is only called once
 * per execution segment on the main JavaScript thread. This is useful when
 * you have a bunch of "changed" events firing as a collection of values get
 * updated, and you want to defer re-drawing the view until all the data
 * is finished changing. Note that the event object your callback is passed
 * will have its data property set to null.
 * @param {string} eventName
 * @param {Foundation.Observable.Event.Handler} callback
 * @return {Foundation.Observable}
 */
Foundation.Observable.prototype.observeCoalesced = function (eventName, callback) {
    this.observe(eventName, this.fireCoalesced.bind(this, callback));
    return this;
};

/**
 * Same as observeCoalesced(), but with the object binding of bind().
 * @param {string} eventName
 * @param {Foundation.Observable} contextObj
 * @param {Foundation.Observable.Event.Handler} method
 * @return {Foundation.Observable}
 */
Foundation.Observable.prototype.bindCoalesced = function (eventName, contextObj, method) {
    this.bind(eventName, contextObj, this.fireCoalesced.bind(this, method));
    return this;
};

/**
 * Fire callbacks registered by observers.
 * @param {string} eventName
 * @param {*=} data
 * @return {Foundation.Observable}
 */
Foundation.Observable.prototype.fire = function (eventName, data) {
    var observers = this._observers[eventName] || [];
    for (var i = 0; i < observers.length; i++) {
        observers[i](new Foundation.Observable.Event(eventName, this, data));
    }
    return this;
};

/**
 * @type {Object}
 */
Foundation.Observable.coalescedCallbacks = {};

/**
 * @type {boolean}
 */
Foundation.Observable.coalescedTimeoutIsSet = false;

/**
 * @param {Foundation.Observable.Event.Handler} callback
 * @param {Foundation.Observable.Event=} e
 */
Foundation.Observable.prototype.fireCoalesced = function (callback, e) {
    if (!Foundation.Observable.coalescedCallbacks[this.identify()]) {
        Foundation.Observable.coalescedCallbacks[this.identify()] = {};
    }
    
    if (!Foundation.Observable.coalescedCallbacks[this.identify()][e.name]) {
        var eventWithoutData = new Foundation.Observable.Event(e.name, this);
        Foundation.Observable.coalescedCallbacks[this.identify()][e.name] = Foundation.curry(callback, eventWithoutData);
    }
    
    if (!Foundation.Observable.coalescedTimeoutIsSet) {
        Foundation.Observable.coalescedTimeoutIsSet = true;
        Foundation.Timer.deferral().then(function () {
            Foundation.Observable.coalescedTimeoutIsSet = false;
            for (var i in Foundation.Observable.coalescedCallbacks) {
                for (var j in Foundation.Observable.coalescedCallbacks[i]) {
                    Foundation.Observable.coalescedCallbacks[i][j]();
                }
            }
        });
    }
};

/**
 * Destructor. Fires the "destroy" event on the object.
 * @protected
 */
Foundation.Observable.prototype.destroy = function () {
    if (!this._hasBeenDestroyed) {
        this.fire("destroy");
        if (this._destructorPromise) {
            this._destructorPromise.fulfill();
        }

        // this._observableData = null;
        this._observers = null;
        this._observersByObj = null;
        this._destructorPromise = null;

        this._hasBeenDestroyed = true;
    }
};

/**
 * Return a promise that will be fulfilled when this object gets destroyed.
 * @returns {Foundation.Promise}
 */
Foundation.Observable.prototype.destroyed = function () {
    if (!this._destructorPromise) {
        this._destructorPromise = new Foundation.Promise();
    }
    
    return this._destructorPromise;
};

/**
 * @param {string} field
 * @param {*} previousValue
 * @param {*} newValue
 */
Foundation.Observable.changedEventData = function (field, previousValue, newValue) {
    return {
        "field": field,
        "previous": previousValue,
        "new": newValue
    };
}

// ---------------------------------------------------------------------

window["Foundation"]["Observable"] = Foundation.Observable;
window["Foundation"]["Observable"].prototype["get"] = Foundation.Observable.prototype.get;
window["Foundation"]["Observable"].prototype["set"] = Foundation.Observable.prototype.set;
window["Foundation"]["Observable"].prototype["unset"] = Foundation.Observable.prototype.unset;
window["Foundation"]["Observable"].prototype["fields"] = Foundation.Observable.prototype.fields;
window["Foundation"]["Observable"].prototype["size"] = Foundation.Observable.prototype.size;
window["Foundation"]["Observable"].prototype["extend"] = Foundation.Observable.prototype.extend;
window["Foundation"]["Observable"].prototype["observe"] = Foundation.Observable.prototype.observe;
window["Foundation"]["Observable"].prototype["unobserve"] = Foundation.Observable.prototype.unobserve;
window["Foundation"]["Observable"].prototype["bind"] = Foundation.Observable.prototype.bind;
window["Foundation"]["Observable"].prototype["unbind"] = Foundation.Observable.prototype.unbind;
window["Foundation"]["Observable"].prototype["fire"] = Foundation.Observable.prototype.fire;
window["Foundation"]["Observable"].prototype["destroy"] = Foundation.Observable.prototype.destroy;
